import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import CaseTimeline from '../components/CaseTimeline';
import { fetchPermits } from '../api/govopsPermitService';
import {
  fetchCase,
  fetchViolations,
  fetchCaseAuditEvents,
  buildCaseTimeline,
  buildCaseTimelineCsv,
  downloadCsv,
} from '../api/govopsOperationsService';
import { formatGovOpsError } from '../api/govopsErrors';
import { formatStatus, formatTimestamp } from '../constants/workflowLabels';
import { printCaseReport } from '../utils/pdfExport';
import PageShell from '../components/PageShell';

const STATUS_CLASS = {
  intake: 'rhs-badge--intake',
  under_review: 'rhs-badge--review',
  notice_sent: 'rhs-badge--notice',
  violation_open: 'rhs-badge--violation',
  payment_pending: 'rhs-badge--payment',
  resolved: 'rhs-badge--resolved',
  closed: 'rhs-badge--closed',
  appealed: 'rhs-badge--appeal',
};

const CaseDetailPage = () => {
  const { caseId } = useParams();
  const [caseRow, setCaseRow] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [c, permits, violations, auditEvents] = await Promise.all([
        fetchCase(caseId),
        fetchPermits(),
        fetchViolations(),
        fetchCaseAuditEvents(caseId),
      ]);
      if (!c) {
        setError('Case not found.');
        setCaseRow(null);
        setEvents([]);
      } else {
        setCaseRow(c);
        setEvents(buildCaseTimeline(c, permits, violations, auditEvents));
      }
    } catch (e) {
      setError(formatGovOpsError(e));
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const csv = buildCaseTimelineCsv(caseRow, events);
      downloadCsv(`robinhoodspace-case-${caseId}-timeline.csv`, csv);
    } catch (e) {
      setError(formatGovOpsError(e));
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <PageShell title="Case timeline">
        <div className="rhs-loading" role="status">
          <span className="rhs-spinner" aria-hidden="true" />
          Loading case…
        </div>
      </PageShell>
    );
  }

  const statusKey = caseRow?.status || 'intake';
  const badgeClass = STATUS_CLASS[statusKey] || 'rhs-badge--default';

  return (
    <PageShell
      title={caseRow?.title || 'Case timeline'}
      subtitle="Audit trail and lifecycle for this false-alarm case."
      className="rhs-case-detail"
    >
      <nav className="rhs-quick-nav" aria-label="Case navigation">
        <Link to="/admin" className="rhs-quick-nav-item">
          ← Operations dashboard
        </Link>
        <Link to="/admin/cases" className="rhs-quick-nav-item">
          All cases
        </Link>
        <Link to="/permit-history" className="rhs-quick-nav-item">
          Permit history
        </Link>
      </nav>

      {error && (
        <div className="rhs-error-panel" role="alert">
          <strong>Error</strong>
          <p className="rhs-muted">{error}</p>
        </div>
      )}

      {caseRow && (
        <>
          <div className="rhs-card">
            <div className="rhs-card-header">
              <h2 className="rhs-card-title">Case overview</h2>
              <span className={`rhs-badge ${badgeClass}`}>
                {formatStatus(caseRow.status)}
              </span>
            </div>
            <dl className="rhs-dl">
              <dt>Case ID</dt>
              <dd className="rhs-permit-id">{caseRow.id}</dd>
              <dt>City / jurisdiction</dt>
              <dd>{caseRow.city || '—'}</dd>
              <dt>Opened</dt>
              <dd>{formatTimestamp(caseRow.createdAt)}</dd>
              <dt>Last updated</dt>
              <dd>{formatTimestamp(caseRow.updatedAt)}</dd>
            </dl>
            <div className="rhs-toolbar-actions">
              <button
                type="button"
                className="rhs-btn rhs-btn--secondary"
                onClick={handleExportCsv}
                disabled={exporting}
              >
                {exporting ? 'Exporting…' : 'Export timeline CSV'}
              </button>
              <button
                type="button"
                className="rhs-btn"
                onClick={() => printCaseReport(caseRow, events)}
              >
                Export report (PDF)
              </button>
            </div>
          </div>

          <section className="rhs-card" aria-labelledby="timeline-heading">
            <h2 id="timeline-heading" className="rhs-card-title">
              Audit &amp; lifecycle
            </h2>
            <p className="rhs-muted rhs-card-lead">
              {events.length} event{events.length === 1 ? '' : 's'} — permits, violations, and status changes.
            </p>
            <CaseTimeline events={events} caseRow={caseRow} />
          </section>
        </>
      )}
    </PageShell>
  );
};

export default CaseDetailPage;
